import type { ReactNode } from 'react'
import { IconaAvviso, IconaFatto, IconaInfo, IconaVuoto } from './Icone.tsx'
// Unica implementazione: quella di base.tsx calcola il colore del testo sulla
// luminanza del fondo, la copia che stava qui usava bianco fisso.
export { Etichetta, EtichettaArea } from './base.tsx'

/** Pezzi ricorrenti dell'interfaccia: intestazioni, avvisi, stati vuoti, attese. */

export function Intestazione({ titolo, sottotitolo, azioni }: {
  titolo: string; sottotitolo?: ReactNode; azioni?: ReactNode
}) {
  return (
    <div className="intestazione-pagina">
      <div>
        <h1>{titolo}</h1>
        {sottotitolo && <p className="sottotitolo">{sottotitolo}</p>}
      </div>
      {azioni && <div className="azioni">{azioni}</div>}
    </div>
  )
}

export function Avviso({ tipo = 'informazione', children }: {
  tipo?: 'informazione' | 'attenzione' | 'errore' | 'successo'; children: ReactNode
}) {
  const Icona = tipo === 'errore' || tipo === 'attenzione' ? IconaAvviso
    : tipo === 'successo' ? IconaFatto : IconaInfo
  return (
    <div className={`avviso ${tipo}`} role={tipo === 'errore' ? 'alert' : undefined}>
      <Icona />
      <div>{children}</div>
    </div>
  )
}

export function Attesa({ testo = 'Caricamento…' }: { testo?: string }) {
  return <div className="caricamento"><span className="girandola" />{testo}</div>
}

export function Vuoto({ testo, azione }: { testo: string; azione?: ReactNode }) {
  return (
    <div className="vuoto">
      <IconaVuoto />
      <p>{testo}</p>
      {azione}
    </div>
  )
}

/**
 * Coppia nome/valore. `mancante`: dato obbligatorio che manca alla scheda (Osservazioni 4 del
 * 23/10/2026), in tono di attenzione e detto a parole, non solo con il colore.
 */
export function Dato({ nome, mancante = false, children }: { nome: string; mancante?: boolean; children: ReactNode }) {
  return (
    <div className={mancante ? 'dato dato-mancante' : 'dato'}>
      <dt>{nome}</dt>
      <dd>
        {children ?? <span className={mancante ? undefined : 'tenue'}>—</span>}
        {mancante && <span className="dato-mancante-nota">Obbligatorio: da completare</span>}
      </dd>
    </div>
  )
}

export function Iniziali({ nome, cognome }: { nome: string; cognome: string }) {
  return <span className="avatar" aria-hidden="true">{(nome[0] ?? '') + (cognome[0] ?? '')}</span>
}

/** Elenco di caselle: i temi, le vulnerabilita', le lingue. Sempre una scelta guidata, mai testo libero. */
export function Caselle({ voci, scelte, cambia, riservate = false }: {
  voci: { codice: string; nome: string; classe_dato?: string }[]
  scelte: string[]
  cambia: (nuove: string[]) => void
  riservate?: boolean
}) {
  return (
    <div className="scelte">
      {voci.map((v) => {
        const delicata = riservate || v.classe_dato === 'riservato' || v.classe_dato === 'giudiziario'
        return (
          <label key={v.codice} className={`scelta${delicata ? ' riservata' : ''}`}>
            <input
              type="checkbox"
              checked={scelte.includes(v.codice)}
              onChange={(e) => cambia(e.target.checked
                ? [...scelte, v.codice]
                : scelte.filter((c) => c !== v.codice))}
            />
            {v.nome}
          </label>
        )
      })}
    </div>
  )
}

/**
 * Pie' di pagina istituzionale.
 *
 * Riporta gli estremi dell'ente titolare del trattamento e i collegamenti alle informative,
 * come previsto per un ente del terzo settore. I dati provengono dal contratto di prestazione
 * d'opera; le informative sono quelle pubblicate su cidisonlus.org.
 *
 * La firma del fornitore resta separata, in coda e in corpo minore: e' un credito tecnico,
 * non parte dell'identita' dell'ente.
 */
export function Pie() {
  return (
    <footer className="pie">
      <div className="pie-ente">
        <span className="denominazione">C.I.D.I.S. Impresa Sociale ETS</span>
        <div className="pie-dati">
          <span>Via della Viola 1, 06122 Perugia</span>
          <span>P.IVA 02695240545</span>
          <span>C.F. 94015970547</span>
          <span>R.E.A. Perugia 258675</span>
        </div>
        <div className="pie-dati">
          <a href="tel:+390755720895">075 5720895</a>
          <a href="mailto:perugia@cidisonlus.org">perugia@cidisonlus.org</a>
        </div>
      </div>

      <nav className="pie-collegamenti" aria-label="Informative">
        <a href="https://cidisonlus.org/info-web/" target="_blank" rel="noopener noreferrer">
          Privacy policy
        </a>
        <a href="https://cidisonlus.org/informativa-destinatari-servizi-del-cidis/"
           target="_blank" rel="noopener noreferrer">
          Informativa destinatari dei servizi
        </a>
        <a href="https://cidisonlus.org/informativa-trattamento-dati-candidati-di-lavoro/"
           target="_blank" rel="noopener noreferrer">
          Informativa candidati
        </a>
        <a href="https://cidisonlus.org/cookie-policy/" target="_blank" rel="noopener noreferrer">
          Cookie policy
        </a>
      </nav>

      <div className="pie-fornitore">
        <a href="https://www.honeymolly.com" target="_blank" rel="noopener noreferrer">
          <img src="/immagini/honey-molly.png" alt="Honey et Molly" />
          <span>
            <span className="pie-fornitore-ruolo">Realizzazione e gestione tecnica</span>
            <span className="pie-fornitore-nome">Honey et Molly</span>
          </span>
        </a>
      </div>
    </footer>
  )
}
